// Клиент `/api/v1/connections/forgejo/*`.
//
// Backend-контракт фиксирован: PAT никогда не возвращается в
// response, токен передаётся только в request body на verify/connect.
// JWT прикручивает `authedFetch` через axios-style interceptor.
//
// Типы вшиты руками параллельно с генерацией openapi-typescript —
// тот же паттерн, что и `features/auth/api.ts`.

import { readErrorDetail } from "../../api/errorDetail";
import { authedFetch } from "../../auth/authedFetch";

// HTTP-коды, которые мы интерпретируем как бизнес-ошибки. Выносим
// в константы — потребители читают `HTTP_UNAUTHORIZED`, а не голую
// 401. Имена совпадают с `features/auth/api.ts` для единообразия.
export const HTTP_UNAUTHORIZED = 401;
// 403 различает allowlist и RBAC-отказ по точной строке `detail`.
export const HTTP_FORBIDDEN = 403;
export const HTTP_NOT_FOUND = 404;
// 409 — Conflict: действие несовместимо с состоянием токена или бота.
export const HTTP_CONFLICT = 409;
// 422 — Unprocessable Entity. Имя совпадает с
// http.HTTPStatus.UNPROCESSABLE_ENTITY на Python-стороне.
export const HTTP_UNPROCESSABLE_ENTITY = 422;
export const HTTP_BAD_GATEWAY = 502;

export interface VerifyForgejoBody {
  readonly host_url: string;
  readonly pat: string;
}

export interface VerifyForgejoResponse {
  readonly username: string;
}

export interface ConnectForgejoBody {
  readonly host_url: string;
  readonly pat: string;
}

export interface ConnectForgejoResponse {
  readonly username: string;
  readonly host_url: string;
}

export interface ForgejoStatusResponse {
  readonly connected: boolean;
  readonly host_url: string | null;
}

export interface ForgejoRepoSummary {
  readonly full_name: string;
  readonly html_url: string;
  readonly private: boolean;
  readonly connected: boolean;
}

export interface ForgejoReposResponse {
  readonly repos: readonly ForgejoRepoSummary[];
}

export interface ConnectRepoBody {
  readonly repo: string;
}

export interface ConnectRepoResponse {
  readonly repo: string;
  readonly webhook_url: string;
}

export interface ConnectedReposResponse {
  readonly repos: readonly string[];
}

export interface DisconnectForgejoResponse {
  readonly disconnected: boolean;
  readonly webhooksRemoved: number;
}

export interface DisconnectForgejoRepoResponse {
  readonly disconnected: boolean;
  readonly webhookRemoved: boolean;
}

/**
 * HTTP-ошибка с числовым `status`. Hooks/мутации различают по нему
 * 401 (неверный PAT) от 5xx (бэк прилёг) без парсинга body.
 */
export class HttpError extends Error {
  public readonly status: number;
  public constructor(status: number, message: string) {
    super(message);
    this.name = "HttpError";
    this.status = status;
  }
}

/**
 * Ошибка ручек Forgejo-подключения. `detail` — фиксированная строка
 * отказа backend'а (`connections/details.py`); кредов и адреса не несёт.
 * `null`, если тело не JSON или `detail` не строка.
 */
export class ForgejoRequestError extends HttpError {
  public readonly detail: string | null;
  public constructor(status: number, message: string, detail: string | null) {
    super(status, message);
    this.detail = detail;
  }
}

export function buildJsonHeaders(): HeadersInit {
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
}

export async function verifyForgejo(
  body: VerifyForgejoBody,
): Promise<VerifyForgejoResponse> {
  // skipAutoLogout: 401 от Forgejo (неверный PAT) — нормальный
  // бизнес-кейс формы, не повод выкидывать user'а из Baton.
  const response = await authedFetch("/api/v1/connections/forgejo/verify", {
    method: "POST",
    headers: buildJsonHeaders(),
    body: JSON.stringify(body),
    skipAutoLogout: true,
  });
  if (!response.ok) {
    throw new ForgejoRequestError(
      response.status,
      `verifyForgejo: HTTP ${String(response.status)}`,
      await readErrorDetail(response),
    );
  }
  return (await response.json()) as VerifyForgejoResponse;
}

export async function connectForgejo(
  body: ConnectForgejoBody,
): Promise<ConnectForgejoResponse> {
  // Каркас connection-модулей: upsert конфига — `PUT /` (idempotent),
  // не `POST /connect`.
  const response = await authedFetch("/api/v1/connections/forgejo", {
    method: "PUT",
    headers: buildJsonHeaders(),
    body: JSON.stringify(body),
    skipAutoLogout: true,
  });
  if (!response.ok) {
    throw new ForgejoRequestError(
      response.status,
      `connectForgejo: HTTP ${String(response.status)}`,
      await readErrorDetail(response),
    );
  }
  return (await response.json()) as ConnectForgejoResponse;
}

export async function getForgejoStatus(): Promise<ForgejoStatusResponse> {
  // Каркас connection-модулей: чтение сохранённого конфига — `GET /`
  // (без сети). `GET /status` теперь live re-probe (сетевой health-чек),
  // UI его не дёргает для определения connected-режима.
  const response = await authedFetch("/api/v1/connections/forgejo", {
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `getForgejoStatus: HTTP ${String(response.status)}`,
    );
  }
  return (await response.json()) as ForgejoStatusResponse;
}

export async function getForgejoRepos(): Promise<ForgejoReposResponse> {
  const response = await authedFetch("/api/v1/connections/forgejo/repos", {
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `getForgejoRepos: HTTP ${String(response.status)}`,
    );
  }
  return (await response.json()) as ForgejoReposResponse;
}

export async function connectForgejoRepo(
  body: ConnectRepoBody,
): Promise<ConnectRepoResponse> {
  const response = await authedFetch("/api/v1/connections/forgejo/repos", {
    method: "POST",
    headers: buildJsonHeaders(),
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `connectForgejoRepo: HTTP ${String(response.status)}`,
    );
  }
  return (await response.json()) as ConnectRepoResponse;
}

export async function getConnectedRepos(): Promise<ConnectedReposResponse> {
  const response = await authedFetch(
    "/api/v1/connections/forgejo/repos/connected",
    {
      headers: { Accept: "application/json" },
    },
  );
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `getConnectedRepos: HTTP ${String(response.status)}`,
    );
  }
  return (await response.json()) as ConnectedReposResponse;
}

// Backend-форма ответа `DELETE /api/v1/connections/forgejo`:
// snake_case `webhooks_removed`. На UI-слое маппим в camelCase, чтобы
// JS-потребитель видел идиоматичный объект.
interface DisconnectForgejoRawResponse {
  readonly disconnected: boolean;
  readonly webhooks_removed: number;
}

interface DisconnectForgejoRepoRawResponse {
  readonly disconnected: boolean;
  readonly webhook_removed: boolean;
}

/**
 * Полное отключение Forgejo для org'и: чистит host_url, PAT,
 * webhook-secret и список репо из ``ISecretStore``, плюс снимает
 * webhook'и на стороне Forgejo (best-effort).
 *
 * Backend idempotent: повторный вызов вернёт ``disconnected=false``
 * без 404. Fail-soft: если Forgejo прилёг, локальный store всё
 * равно очистится, ``webhooksRemoved`` отразит фактическое число.
 */
export async function disconnectForgejo(): Promise<DisconnectForgejoResponse> {
  const response = await authedFetch("/api/v1/connections/forgejo", {
    method: "DELETE",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `disconnectForgejo: HTTP ${String(response.status)}`,
    );
  }
  const raw = (await response.json()) as DisconnectForgejoRawResponse;
  return {
    disconnected: raw.disconnected,
    webhooksRemoved: raw.webhooks_removed,
  };
}

/**
 * Отвязка одного репо: удаляет ``owner/name`` из
 * ``connected_repos`` org'и и снимает webhook на стороне Forgejo.
 * host_url / PAT / webhook-secret org'и не трогаются (общие для всех
 * репо). Idempotent: повторный DELETE → ``disconnected=false``.
 *
 * ``repo`` встраивается в URL как path (``{repo:path}``); сегмент
 * ``owner/name`` валидируется backend-regex'ом, защищающим от
 * path-injection. Здесь не encode'им — слеш является частью валидного
 * значения, encoded ``%2F`` backend не примет.
 */
export async function disconnectForgejoRepo(
  repo: string,
): Promise<DisconnectForgejoRepoResponse> {
  const response = await authedFetch(
    `/api/v1/connections/forgejo/repos/${repo}`,
    {
      method: "DELETE",
      headers: { Accept: "application/json" },
    },
  );
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `disconnectForgejoRepo: HTTP ${String(response.status)}`,
    );
  }
  const raw = (await response.json()) as DisconnectForgejoRepoRawResponse;
  return {
    disconnected: raw.disconnected,
    webhookRemoved: raw.webhook_removed,
  };
}

// --- Coder-система (ADR-0074). ------------------
//
// Организация выбирает in-runner-кодер (`coder_kind`) из статического
// каталога `/providers`. Без явного выбора `GET /coder-system` отдаёт
// `coder_kind: null` — ни один кодер не подсвечен, задачи не
// запускаются. Контракт — `modules/provider/schemas.py`.
//
// У backend нет канала к бинарям раннера: `GET /status` всегда отвечает
// `200` с `runner_available=false` и пустым `coders`. Это не отказ —
// секция рисует «наличие бинаря не проверено» и даёт сохранить путь без
// проверки.
//
// `resolved_path` — путь ФС (не секрет), отдаётся в ответе.

// Статус обнаружения одного кодера на runner'е. Зеркалит
// доменный CoderProbeResult.status: not_found / unhealthy / healthy /
// unspecified (рассинхрон proto-enum, UI трактует как недоступен).
export type CoderProbeStatus =
  | "not_found"
  | "unhealthy"
  | "healthy"
  | "unspecified";

export interface InstallCommand {
  // Площадка/способ установки: 'linux' / 'macos' / 'windows' / 'npm'.
  readonly platform: string;
  readonly command: string;
}

export interface InstallHint {
  readonly commands: readonly InstallCommand[];
  readonly url: string;
}

export interface CoderProviderResponse {
  readonly coder_kind: string;
  readonly install_hint: InstallHint;
  readonly models: readonly string[];
  readonly max_context_tokens: number;
}

export interface CoderProbeStatusResponse {
  readonly coder_kind: string;
  readonly status: CoderProbeStatus;
  readonly resolved_path: string | null;
  readonly version: string | null;
  readonly error_message: string | null;
}

export interface CoderSystemStatusResponse {
  readonly runner_available: boolean;
  readonly coders: readonly CoderProbeStatusResponse[];
}

export interface CoderSystemConfigResponse {
  readonly coder_kind: string | null;
  readonly override_path: string | null;
  readonly is_explicit: boolean;
}

export interface SetCoderSystemBody {
  readonly coder_kind: string;
  // null/пустая строка — полагаемся на auto-discovery (binary_candidates).
  readonly override_path?: string | null;
}

export async function getCoderProviders(): Promise<
  readonly CoderProviderResponse[]
> {
  const response = await authedFetch(
    "/api/v1/connections/coder-system/providers",
    { headers: { Accept: "application/json" } },
  );
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `getCoderProviders: HTTP ${String(response.status)}`,
    );
  }
  return (await response.json()) as readonly CoderProviderResponse[];
}

export async function getCoderSystemStatus(): Promise<CoderSystemStatusResponse> {
  const response = await authedFetch(
    "/api/v1/connections/coder-system/status",
    { headers: { Accept: "application/json" } },
  );
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `getCoderSystemStatus: HTTP ${String(response.status)}`,
    );
  }
  return (await response.json()) as CoderSystemStatusResponse;
}

export async function getCoderSystem(): Promise<CoderSystemConfigResponse> {
  const response = await authedFetch("/api/v1/connections/coder-system", {
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `getCoderSystem: HTTP ${String(response.status)}`,
    );
  }
  return (await response.json()) as CoderSystemConfigResponse;
}

export async function setCoderSystem(
  body: SetCoderSystemBody,
): Promise<CoderSystemConfigResponse> {
  const response = await authedFetch("/api/v1/connections/coder-system", {
    method: "PUT",
    headers: buildJsonHeaders(),
    body: JSON.stringify(body),
    skipAutoLogout: true,
  });
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `setCoderSystem: HTTP ${String(response.status)}`,
    );
  }
  return (await response.json()) as CoderSystemConfigResponse;
}

/**
 * Сброс выбора кодера организации: `DELETE /coder-system`. Backend
 * idempotent — `204` и без сохранённого выбора.
 */
export async function deleteCoderSystem(): Promise<void> {
  const response = await authedFetch("/api/v1/connections/coder-system", {
    method: "DELETE",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `deleteCoderSystem: HTTP ${String(response.status)}`,
    );
  }
}
